/**
 * Loan terms as the catalogue holds them, read one way for every dossier tab.
 *
 * The Overview, Economics and Capital Stack tabs each read the deal's terms
 * for themselves, and each read keys the catalogue does not have: `product`,
 * `term`, `io_period`, `extension_options`, `origination_fee`, `exit_fee`.
 * Only the seeded control deal carried those, so every deal intake produced
 * showed a dash for a term, IO period, fee or amortization it holds under its
 * catalogue name (`term_months`, `io_months`, `extensions`,
 * `origination_fee_bps`, `exit_fee_bps`, `amortization_type`). These readers
 * take the catalogue name first; a legacy key is read only as a fallback so a
 * record written before the catalogue existed still shows what it holds.
 *
 * Presentation only. Nothing here derives a figure: a value that is not on
 * the record is `null`, and the caller shows it as missing.
 */

import { DEFAULT_POSITION } from '@/components/intake/intakeDefaults'
import { measureDisplayLabel } from '@/components/intake/measureDefaults'
import { formatPct } from './format'

export type TermGetter = (name: string) => unknown

function present(value: unknown): boolean {
  if (value === null || value === undefined) return false
  const text = String(value).trim()
  return text !== '' && text !== '—'
}

function text(value: unknown): string | null {
  return present(value) ? String(value).trim() : null
}

function finite(value: unknown): number | undefined {
  if (!present(value)) return undefined
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s]/g, ''))
  return Number.isFinite(n) ? n : undefined
}

function first(get: TermGetter, names: string[]): unknown {
  for (const name of names) {
    const value = get(name)
    if (present(value)) return value
  }
  return undefined
}

/** A month count as a reader says it. Prose already naming its unit passes through. */
export function monthsLabel(value: unknown): string | null {
  if (!present(value)) return null
  const n = finite(value)
  if (n === undefined) return String(value).trim()
  return `${n} mo`
}

/** A fee held in basis points ("100") — or, on a legacy record, stated as prose ("1.00%"). */
export function bpsLabel(value: unknown): string | null {
  if (!present(value)) return null
  const n = finite(value)
  if (n === undefined) return String(value).trim()
  return `${n} bps`
}

/**
 * A spread as a reader says it.
 *
 * Stated text ("350 bps", "S+285") is shown as stated. A bare number is on one
 * of the scales the record has been seen to hold — a fraction (0.0295), a
 * percent (2.95) or basis points (295) — and is shown in basis points so the
 * pipeline can print it as a percent (`pipelinePercentText`).
 */
export function spreadLabel(value: unknown): string | null {
  if (!present(value)) return null
  const n = finite(value)
  if (n === undefined) return String(value).trim()
  const bps = Math.abs(n) < 0.2 ? n * 10_000 : Math.abs(n) < 20 ? n * 100 : n
  return `${Number(bps.toFixed(1))} bps`
}

/** The all-in rate as the record states it: "SOFR + 350 bps" stays text; 0.0635 is 6.35%. */
export function rateText(value: unknown): string | null {
  if (!present(value)) return null
  const n = finite(String(value).replace('%', ''))
  if (n === undefined) return String(value).trim()
  if (typeof value === 'string' && value.includes('%')) return value.trim()
  return formatPct(n, '') || null
}

/** Where the product is read from, first to last: the catalogue field
 *  `loan_type` ("Product / Loan Type"), the legacy `product` key a record
 *  written before the catalogue may still carry, then the capital program. */
const PRODUCT_FIELDS = ['loan_type', 'product', 'capital_program'] as const

/**
 * The deal's product / loan type, and the field it was read from.
 *
 * Every surface read it differently: Economics read the legacy `product`
 * before `loan_type`, so a saved Loan Type edit kept showing the old value on
 * any record that still carried `product`; the Overview, the Capital Stack
 * tranche and the profile never read `loan_type` at all.
 */
export function productField(get: TermGetter): { value: string; fieldName: string } | null {
  for (const name of PRODUCT_FIELDS) {
    const value = text(get(name))
    if (value) return { value, fieldName: name }
  }
  return null
}

/** The deal's product / loan type as one label, or null when none is held. */
export function productLabel(get: TermGetter): string | null {
  return productField(get)?.value ?? null
}

/** Initial term, with any extension months beside it: "36 mo + 12 mo ext.". */
export function termLabel(get: TermGetter): string | null {
  const term = monthsLabel(first(get, ['term_months', 'term']))
  const ext = monthsLabel(get('extensions'))
  if (!term) return null
  return ext ? `${term} + ${ext} ext.` : term
}

/**
 * How the loan amortizes, from the stated type, amortization months and IO months.
 *
 * `amortization_type` alone left "IO - Interest Only" on a loan whose term
 * sheet also states a 24-month IO and a 30-year schedule; the three together
 * say what the loan actually does.
 */
export function amortizationLabel(get: TermGetter): string | null {
  const type = text(get('amortization_type')) ?? text(get('amortization'))
  const months = finite(get('amortization_months'))
  const io = finite(first(get, ['io_months', 'io_period', 'interest_only_months']))
  const years = months != null && months > 0 ? Number((months / 12).toFixed(1)) : undefined
  if (years != null && io != null && io > 0) return `${io} mo IO, then ${years}-yr amortization`
  if (years != null) return `${years}-yr amortization`
  if (type && /interest[\s-]*only|^io\b/i.test(type) && !/partial/i.test(type)) {
    return io != null && io > 0 ? `Interest only (${io} mo)` : 'Interest only'
  }
  if (type && io != null && io > 0) return `${type} (${io} mo IO)`
  return type
}

export type StackKind = 'senior' | 'mezz' | 'pref' | 'other'

export type StackPosition = {
  /** What the record calls the tranche: "Mezzanine", "Senior", "B-Note". */
  label: string
  kind: StackKind
  lien: string | null
  /** A tranche with debt ahead of it. Its leverage and debt yield are only
   *  meaningful through the debt ahead, which the record may not state. */
  subordinate: boolean
  /** True when the record states neither a position nor a lien. */
  assumed: boolean
}

/**
 * Where the requested tranche sits in the stack.
 *
 * Every tab drew the loan as "Senior" when no stack layers were stored,
 * ignoring the `position` and `lien_position` the record holds — so a $9.6M
 * mezzanine note behind an unstated senior loan was drawn as 100% of the
 * stack, senior.
 *
 * Senior is also what a deal now STARTS with: the platform stores it at
 * creation and promotion where nothing says otherwise, recorded as a default
 * (`source = "default"`) that a stated position or a person's pick replaces
 * (client item #1; `app/services/intake_defaults.py`). That is a stored value,
 * read like any other — `position` first, the lien deciding `subordinate` when
 * the two disagree, exactly as the engine's `is_subordinate` does. The fallback
 * below is only for a record that holds no position at all (an older deal), and
 * it is the same word, `DEFAULT_POSITION`. (Under `POSITION_DEFAULT=whole_loan`
 * a NEW deal starts as Whole Loan, stored; this fallback stays Senior, because
 * the default is never backfilled onto a deal that exists.)
 */
export function stackPosition(get: TermGetter): StackPosition {
  const position = text(get('position'))
  const lien = text(get('lien_position'))
  const p = (position ?? '').toLowerCase()
  const l = (lien ?? '').toLowerCase()
  let kind: StackKind
  if (/mezz|b[\s-]?note|junior|subordinate/.test(p)) kind = 'mezz'
  else if (/pref/.test(p)) kind = 'pref'
  else if (/senior|a[\s-]?note|whole|first/.test(p)) kind = 'senior'
  else if (p) kind = 'other'
  else if (/second|mezz|junior|subordinate/.test(l)) kind = 'mezz'
  else kind = 'senior'
  const subordinate =
    kind === 'mezz' || kind === 'pref' || /second|third|mezz|junior|subordinate/.test(l)
  const label = position ?? (subordinate ? 'Subordinate note' : DEFAULT_POSITION)
  return { label, kind, lien, subordinate, assumed: !position && !lien }
}

/** "Mezzanine · Second lien" — the position and the lien as the record states them. */
export function positionLabel(get: TermGetter): string | null {
  const position = text(get('position'))
  const lien = text(get('lien_position'))
  if (!position && !lien) return null
  const lienText = lien ? (/lien/i.test(lien) ? lien : `${lien} lien`) : null
  return [position, lienText].filter(Boolean).join(' · ')
}

/**
 * The unit count and what it counts: "212 units", "148 rooms".
 *
 * A square-foot "unit of measure" is not a count of units, so it is left to
 * the area row rather than printed here as "310,000 sf units".
 */
export function unitCountLabel(get: TermGetter): string | null {
  const units = finite(get('units'))
  if (units == null || units <= 0) return null
  const measure = text(get('unit_of_measure')) ?? 'Units'
  // `sf` and `sm` state an AREA; neither is a count of units (sm is never converted to sf).
  if (['sf', 'sm'].includes(measure.toLowerCase())) return null
  // A hotel counts keys: `Rooms` is the stored word and "keys" the label (display
  // only — client item #1), and land is counted in acres.
  return `${units.toLocaleString()} ${measureDisplayLabel(measure, get('asset_type')).toLowerCase()}`
}

/** Stages at which a credit committee has decided on the deal. */
const DECIDED_STAGES = new Set(['IC / Committee Approved', 'Rate Lock', 'Funded'])

export const NOT_YET_APPROVED = 'Not yet approved'

/**
 * Whether a committee has decided, and what it decided.
 *
 * An "Approved Amount" and a "Max LTV" were filled from the loan request and
 * the deal's current LTV on every Triage deal, so a record no committee had
 * seen claimed one had approved it. An approval exists only once the deal has
 * reached a committee stage; before that there is nothing to show. After it,
 * the figures are the recorded approval terms — never the request, and never
 * the deal's own LTV restated as a limit.
 */
export function committeeDecision(get: TermGetter, stage: unknown): {
  decided: boolean
  approvedAmount: number | undefined
  maxLtv: number | undefined
} {
  const decided = DECIDED_STAGES.has(String(stage ?? '').trim())
  if (!decided) return { decided, approvedAmount: undefined, maxLtv: undefined }
  return {
    decided,
    approvedAmount: finite(get('approved_amount')),
    maxLtv: finite(get('max_ltv')),
  }
}

/** What a subordinate note's debt yield or LTV reads as when the debt ahead is not stated. */
export const SENIOR_NOT_STATED = 'n/a — senior debt not stated'

/**
 * A subordinate tranche whose senior debt the record does not state.
 *
 * Debt yield and leverage on a mezzanine note are measured through the debt
 * ahead of it. NOI over the note alone (Stanhope Row: $1.95M over $9.6M,
 * 20.31%) looks four times safer than the position is, so until the senior
 * amount is on the record the figure is not shown.
 */
export function seniorAheadUnstated(get: TermGetter, storedLayers?: unknown): boolean {
  if (!stackPosition(get).subordinate) return false
  const layers = Array.isArray(storedLayers) ? storedLayers : []
  return !layers.some((layer) => {
    if (!layer || typeof layer !== 'object') return false
    const row = layer as Record<string, unknown>
    const name = String(row.type ?? row.position ?? row.component ?? row.name ?? '')
    return /senior|first mortgage|a[\s-]?note/i.test(name) && finite(row.amount) != null
  })
}
